import { AxiosResponse } from "axios";
import { CourierPickupSummary, PickupDashboardMonitoringCourierSummary, AvailableCourierList, AvailableCourier, SenderList, SenderData, CourierPickupShipment, PickupDashboardMonitoringCourierShipment, PickupDashboardMonitoringCourierPickupEntities, PickupDashboardMonitoringCourierData, PickupDashboardMonitoringCourierPickup, ManualAssignResponse, ManualAssignData } from "@/domain/entities/PickupDashboard";
import { ResponsePayloadV2 } from "@/domain/entities/ResponsePayload";
import { Pagination, PaginationV2 } from "@/domain/entities/Pagination";

import {
  ListSummaryItem,
  PickupDashboardMonitoringDetailSummary,
  TabsData,
  TabsList,
} from "@/domain/entities/PickupDashboardSummary";

export class PickupDashboardMapper {
   private statusMap: Record<string, string> = {
    CRROTW: "Belum Di Pick Up",
    CRRPIC: "Belum Disetor",
    CRRDON: "Belum BKD",
    CRRNFD: "Belum Diassign",
  };

  private toTitleCaseFromSnake(str: string): string {
    return str
      .toLowerCase()
      .split("_")
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }


  public convertTabsListFromApi(res: AxiosResponse): TabsList {
    const { data } = res;

    return new TabsList({
      data: data.data?.length
        ? data.data.map(
            (item: any) =>
              new TabsData({
                status: item.status,
                seller: item.seller,
                courier: item.courier,
                shipment: item.shipment,
                quantity: item.quantity,
                statusLabel: this.statusMap[item.status] ?? item.status,
              })
          )
        : [],
    });
  }

  public convertCourierSummaryFromApi(res: AxiosResponse): CourierPickupSummary {
    const { data } = res;
    const list: PickupDashboardMonitoringCourierSummary[] = Array.isArray(data?.data)
      ? data.data.map((item: any) =>
          new PickupDashboardMonitoringCourierSummary({
            status: item.status || "",
            seller: Number(item.seller ?? 0),
            courier: Number(item.courier ?? 0),
            shipment: Number(item.shipment ?? 0),
            quantity: Number(item.quantity ?? 0)
          })
        )
      : [];
    return new CourierPickupSummary({ data: list });
  }

  public convertAvailableCouriersFromApi(res: AxiosResponse): AvailableCourierList {
    const { data } = res;
    const list: AvailableCourier[] = Array.isArray(data?.data)
      ? data.data.map((item: any) => new AvailableCourier({
          courierId: item.courier_id,
          courierName: item.courier_name,
          courierPhone: item.courier_phone,
          courierType: item.courier_type
        }))
      : [];
    return new AvailableCourierList({ data: list });
  }

  public convertTransferPickupFromApi(result: AxiosResponse<any>): ResponsePayloadV2 {
    const { data } = result;
    return new ResponsePayloadV2({
      code: data.code,
      success: data.success,
      message: data.message,
      data: data.data // Include the response data
    });
  }

  public convertExportFromApi(result: AxiosResponse<any>): ResponsePayloadV2 {
    const { data } = result;
    return new ResponsePayloadV2({
        code: data.code,
        success: data.success,
        message: data.message
    });
  }

  public convertSenderListFromApi(res: AxiosResponse): SenderList {
    const { data } = res;
    const meta = data?.meta || {};
    const list: SenderData[] = Array.isArray(data?.data)
      ? data.data.map((item: any) => new SenderData({
          id: item.id,
          courierId: item.courier_id,
          courierName: item.courier_name,
          courierPhone: item.courier_phone,
          senderName: item.sender_name,
          senderPhone: item.sender_phone,
          senderAddress: item.sender_address,
          shipmentType: item.shipment_type,
          shipmentId: item.shipment_id,
          totalShipment: item.total_shipment,
          totalQuantity: item.total_quantity,
          totalWeight: item.total_weight,
          expiredAt: item.expired_at,
          status: item.status,
          statusAt: item.status_at,
          proofs: item.proofs,
          isAbleToBooking: item.is_able_to_booking,
          lat: item.lat,
          lon: item.lon,
          taskGroupId: item.task_group_id
        }))
      : [];
    
    return new SenderList({
      data: list,
      meta: {
        totalRecords: meta.total_records,
        currentPage: meta.page,
        totalPages: meta.total_pages,
        limit: meta.limit
      }
    });
  }
  public convertListSummaryFromApi(
    result: AxiosResponse<any>
  ) {
    const { data, meta } = result.data;
    return new PickupDashboardMonitoringDetailSummary({
      data: data
        ? data.map((item: any) => {
            return new ListSummaryItem({
              courierId: item.courier_id,
              courierName: item.courier_name,
              courierPhone: item.courier_phone,

              senderName: item.sender_name,
              senderPhone: item.sender_phone,
              senderAddress: item.sender_address,

              shipmentType: this.toTitleCaseFromSnake(item.shipment_type),
              shipmentId: item.shipment_id,

              totalShipment: item.total_shipment,
              totalQuantity: item.total_quantity,
              totalWeight: item.total_weight,

              expiredAt: item.expired_at,
              status: item.status,
              statusAt: item.status_at,

              proofs: item.proofs,
              isAbleToBooking: item.is_able_to_booking,

              lat: item.lat,
              lon: item.lon,

              taskId: item.task_group_id,
            });
          })
        : [],
      pagination: new Pagination(meta.page, meta.limit, meta.total_records)
    });
  }

  public convertShipmentListFromApi(res: AxiosResponse): ResponsePayloadV2 {
    const { data } = res;
    const meta = data?.meta || {};
    const list = Array.isArray(data?.data) ? data.data : [];
    return new ResponsePayloadV2({
      pagination: new PaginationV2({
        page: meta.page,
        limit: meta.limit,
        totalData: meta.total_records
      }),
      data: list
    })
  }

  public convertCourierShipmentFromApi(res: AxiosResponse): CourierPickupShipment {
    const { data } = res;
    return new CourierPickupShipment({
      pagination: new PaginationV2({
        page: data.meta.page,
        limit: data.meta.limit,
        totalData: data.meta.total_records
      }),
      data: data.data.length
        ? data.data.map((item: any) => {
            return new PickupDashboardMonitoringCourierShipment({
              courierId: item.courier_id,
              courierName: item.courier_name,
              courierPhone: item.courier_phone,
              senderName: item.sender_name,
              senderPhone: item.sender_phone,
              senderAddress: item.sender_address,
              shipmentType: item.shipment_type,
              shipmentId: item.shipment_id,
              totalShipment: item.total_shipment,
              totalQuantity: item.total_quantity,
              totalWeight: item.total_weight,
              statusAt: item.status_at,
              status: item.status,
              proofs: item.proofs,
              isAbleToBooking: item.is_able_to_booking,
              lat: item.lat,
              lon: item.lon,
              taskGroupId: item.task_group_id
            });
          })
        : []
    });
  }

  public convertCourierListFromApi(res: AxiosResponse) {
    const {data: {data, meta}} = res;

    const courierList = Array.isArray(data) ? data : [];

    return new PickupDashboardMonitoringCourierPickupEntities({
      data: courierList.map((courier: any) => {
        const pickupList = Array.isArray(courier.pickup) ? courier.pickup : [];

        return new PickupDashboardMonitoringCourierData({
          courierId: courier.courier_id,
          courierName: courier.courier_name,
          courierPhone: courier.courier_phone,
          pickup: pickupList.map((pickup: any) => {
            return new PickupDashboardMonitoringCourierPickup({
              isWithinTimeLimit: pickup.is_within_timelimit,
              quantity: pickup.quantity,
              seller: pickup.seller,
              shipment: pickup.shipment,
              status: pickup.status
            })
          })
        })
      }),
      pagination: new Pagination(meta.page, meta.limit, meta.total_records)
    })
  }

  public convertManualAssignFromApi(result: AxiosResponse<any>): ManualAssignResponse {
    const { data } = result;
    return new ManualAssignResponse({
      data: data.data.length
        ? data.data.map((item: any) => {
            return new ManualAssignData({
              courierId: item.courier_id,
              courierName: item.courier_name,
              courierPhone: item.courier_phone,
              senderName: item.sender_name,
              senderPhone: item.sender_phone,
              senderAddress: item.sender_address,
              shipmentType: item.shipment_type,
              shipmentId: item.shipment_id,
              totalShipment: item.total_shipment,
              totalQuantity: item.total_quantity,
              totalWeight: item.total_weight,
              expiredAt: item.expired_at,
              statusAt: item.status_at,
              status: item.status,
              proofs: item.proofs,
              isAbleToBooking: item.is_able_to_booking,
              lat: item.lat,
              lon: item.lon,
              taskGroupId: item.task_group_id,
              isSuccess: item.is_success
            });
          })
        : []
    });
  }
}
